import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  useTheme,
  alpha,
} from '@mui/material';
import moment from 'moment';
import api from '../services/api';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TypeSpan = styled('span')(({ theme, type }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  backgroundColor: 
    type === 'USED' ? theme.palette.error.light :
    type === 'RETRIEVED' ? theme.palette.success.light :
    'transparent',
  color: 
    type === 'USED' || type === 'RETRIEVED' ? theme.palette.common.white :
    theme.palette.text.primary,
}));

const UpdateSuperReseller = () => {
  const { resellerName } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [resellerData, setResellerData] = useState({
    name: '',
    credits: 0,
    description: '',
  });
  const [transactions, setTransactions] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created');
  const [order, setOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [creditsAction, setCreditsAction] = useState(0);
  const [creditsAmount, setCreditsAmount] = useState(1);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [statisticsPage, setStatisticsPage] = useState(0);
  const [statisticsRowsPerPage, setStatisticsRowsPerPage] = useState(10);
  const [statisticsOrderBy, setStatisticsOrderBy] = useState('onDate');
  const [statisticsOrder, setStatisticsOrder] = useState('desc');
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    if (resellerName) {
      fetchResellerData();
      fetchTransactions();
      fetchStatistics();
      fetchChartData();
    }
  }, [resellerName]);

  const fetchResellerData = async () => {
    try {
      const response = await api.get(`/admin/superreseller/${resellerName}`);
      setResellerData(prevData => ({
        ...prevData,
        ...response.data,
        name: resellerName
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reseller data:', error);
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get(`/admin/history/${resellerName}`);  // Updated API endpoint
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get(`/admin/statistics/${resellerName}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await api.get(`/admin/statschart/${resellerName}`);
      const data = response.data;
      
      setChartData({
        labels: data.onDate,
        datasets: [
          {
            label: 'Clients',
            data: data.clients,
            fill: false,
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1
          }
        ]
      });
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setResellerData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await api.post('/admin/save', {
        name: resellerName,
        credits: creditsAmount,
        desc: resellerData.description,
        transaction: creditsAction
      });
      if (response.data.status === 'OK') {
        setSnackbar({
          open: true,
          message: 'Super Reseller updated successfully!',
          severity: 'success'
        });
        fetchResellerData();
        fetchTransactions();
        setCreditsAmount(1);
        setCreditsAction(0);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error updating super reseller:', error);
      setSnackbar({
        open: true,
        message: 'Error updating super reseller. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const filteredTransactions = transactions.filter(transaction =>
    Object.values(transaction).some(value =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedTransactions = filteredTransactions.sort((a, b) => {
    if (b[orderBy] < a[orderBy]) {
      return order === 'asc' ? 1 : -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return order === 'asc' ? -1 : 1;
    }
    return 0;
  });

  const handleStatisticsChangePage = (event, newPage) => {
    setStatisticsPage(newPage);
  };

  const handleStatisticsChangeRowsPerPage = (event) => {
    setStatisticsRowsPerPage(parseInt(event.target.value, 10));
    setStatisticsPage(0);
  };

  const handleStatisticsRequestSort = (property) => {
    const isAsc = statisticsOrderBy === property && statisticsOrder === 'asc';
    setStatisticsOrder(isAsc ? 'desc' : 'asc');
    setStatisticsOrderBy(property);
  };

  const sortedStatistics = React.useMemo(() => {
    const comparator = (a, b) => {
      if (b[statisticsOrderBy] < a[statisticsOrderBy]) {
        return statisticsOrder === 'asc' ? 1 : -1;
      }
      if (b[statisticsOrderBy] > a[statisticsOrderBy]) {
        return statisticsOrder === 'asc' ? -1 : 1;
      }
      return 0;
    };

    return [...statistics].sort(comparator);
  }, [statistics, statisticsOrder, statisticsOrderBy]);

  const headCells = [
    { id: 'created', label: 'Date' },
    { id: 'type', label: 'Type' },
    { id: 'credits', label: 'Credits', numeric: true },
    { id: 'description', label: 'Description' },
    { id: 'expiry', label: 'Expiry' },
  ];

  const statisticsHeadCells = [
    { id: 'onDate', label: 'Date' },
    { id: 'credits', label: 'Credits', numeric: true },
    { id: 'resellers', label: 'Resellers', numeric: true },
    { id: 'dealers', label: 'Dealers', numeric: true },
    { id: 'clients', label: 'Clients', numeric: true },
  ];

  const creditOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1500, 2000, 2500, 5000, 10000];

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await api.post('/admin/changepassword', {
        name: resellerName,
        password: newPassword
      });

      if (response.data.status === 'OK') {
        setSnackbar({
          open: true,
          message: 'Password changed successfully!',
          severity: 'success'
        });
        setNewPassword('');
        setConfirmPassword('');
      } else {
        throw new Error('Password change failed');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setSnackbar({
        open: true,
        message: 'Error changing password. Please try again.',
        severity: 'error'
      });
    }
  };

  const cardStyle = {
    boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
    transition: '0.3s',
    '&:hover': {
      boxShadow: '0 8px 16px 0 rgba(0,0,0,0.2)',
    },
  };

  const cardHeaderStyle = {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.common.black,
    padding: '16px',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  };

  const cardTitleStyle = {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: theme.palette.common.black,
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Number of Clients Over Time',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Clients'
        },
        beginAtZero: true
      }
    }
  };

  return (
    <Box sx={{ width: '100%', padding: 3 }}>
      <Grid container spacing={3}>
        {/* Update Super Reseller Card */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardHeader 
              title={`Update ${resellerName}`}
              subheader={`Credits: ${resellerData.credits}`}
              titleTypographyProps={{ variant: 'h6', ...cardTitleStyle }}
              sx={{
                ...cardHeaderStyle,
                '& .MuiCardHeader-action': {
                  alignSelf: 'center',
                },
              }}
            />
            <Divider />
            <CardContent>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credits Action</InputLabel>
                <Select
                  value={creditsAction}
                  onChange={(e) => setCreditsAction(e.target.value)}
                >
                  <MenuItem value={0}>Add</MenuItem>
                  <MenuItem value={1}>Remove</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credits Amount</InputLabel>
                <Select
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(e.target.value)}
                >
                  {creditOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                margin="normal"
                label="Description"
                name="description"
                multiline
                rows={4}
                value={resellerData.description}
                onChange={handleInputChange}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ mt: 2 }}
              >
                Update Super Reseller
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password Card */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardHeader 
              title="Change Password"
              titleTypographyProps={{ variant: 'h6', ...cardTitleStyle }}
              sx={cardHeaderStyle}
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                margin="normal"
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handlePasswordChange}
                disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
                sx={{ mt: 2 }}
              >
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Transactions Card */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardHeader 
              title="Transactions"
              titleTypographyProps={{ variant: 'h6', ...cardTitleStyle }}
              action={
                <TextField
                  label="Search Transactions"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ width: 200 }}
                />
              }
              sx={{
                ...cardHeaderStyle,
                display: 'flex',
                alignItems: 'center',
                '& .MuiCardHeader-action': {
                  margin: 0,
                  alignSelf: 'center',
                },
              }}
            />
            <Divider />
            <CardContent>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.numeric ? 'right' : 'left'}
                          sortDirection={orderBy === headCell.id ? order : false}
                        >
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={() => handleRequestSort(headCell.id)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : sortedTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedTransactions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((transaction, index) => (
                          <TableRow key={index}>
                            <TableCell>{moment(transaction.created).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                            <TableCell>
                              <TypeSpan type={transaction.type}>
                                {transaction.type}
                              </TypeSpan>
                            </TableCell>
                            <TableCell align="right">{transaction.credits}</TableCell>
                            <TableCell>{transaction.description}</TableCell>
                            <TableCell>{moment(transaction.expiry).format('YYYY-MM-DD')}</TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredTransactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics History Card */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardHeader 
              title="Statistics History"
              titleTypographyProps={{ variant: 'h6', ...cardTitleStyle }}
              sx={cardHeaderStyle}
            />
            <Divider />
            <CardContent>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      {statisticsHeadCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.numeric ? 'right' : 'left'}
                          sortDirection={statisticsOrderBy === headCell.id ? statisticsOrder : false}
                        >
                          <TableSortLabel
                            active={statisticsOrderBy === headCell.id}
                            direction={statisticsOrderBy === headCell.id ? statisticsOrder : 'asc'}
                            onClick={() => handleStatisticsRequestSort(headCell.id)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : sortedStatistics.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No statistics found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedStatistics
                        .slice(statisticsPage * statisticsRowsPerPage, statisticsPage * statisticsRowsPerPage + statisticsRowsPerPage)
                        .map((stat, index) => (
                          <TableRow key={index}>
                            <TableCell>{moment(stat.onDate).format('YYYY-MM-DD')}</TableCell>
                            <TableCell align="right">{stat.credits}</TableCell>
                            <TableCell align="right">{stat.resellers}</TableCell>
                            <TableCell align="right">{stat.dealers}</TableCell>
                            <TableCell align="right">{stat.clients}</TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={statistics.length}
                rowsPerPage={statisticsRowsPerPage}
                page={statisticsPage}
                onPageChange={handleStatisticsChangePage}
                onRowsPerPageChange={handleStatisticsChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Chart Card */}
        <Grid item xs={12}>
          <Card sx={cardStyle}>
            <CardHeader 
              title="Clients History"
              titleTypographyProps={{ variant: 'h6', ...cardTitleStyle }}
              sx={cardHeaderStyle}
            />
            <Divider />
            <CardContent>
              <Box sx={{ height: 400 }}>
                <Line options={chartOptions} data={chartData} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpdateSuperReseller;